import { useEffect, useRef, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import clsx from 'clsx';
import { Building2, UserRound } from 'lucide-react';
import type { Order } from '@sebastian/shared';
import { api, ApiError } from '../lib/api';
import { lookupCompanyByTaxCode, TAX_CODE_PATTERN } from '../lib/taxLookup';
import { useToast } from '../hooks/useToast';
import { Button, Field, Input, Modal } from './ui';

type Draft = { company: string; name: string; taxCode: string; address: string; email: string };
const EMPTY: Draft = { company: '', name: '', taxCode: '', address: '', email: '' };

const draftFromOrder = (order: Order): Draft => ({
  company: order.invoiceBuyer?.company ?? '',
  name: order.invoiceBuyer?.name ?? '',
  taxCode: order.invoiceBuyer?.taxCode ?? '',
  address: order.invoiceBuyer?.address ?? '',
  email: order.invoiceBuyer?.email ?? '',
});

const draftToBody = (value: Draft) => ({
  company: value.company.trim() || null,
  name: value.name.trim() || null,
  taxCode: value.taxCode.trim() || null,
  address: value.address.trim() || null,
  email: value.email.trim() || null,
});

type Mode = 'RETAIL' | 'COMPANY';
type LookupState = 'idle' | 'loading' | 'found' | 'notFound' | 'failed';

const LOOKUP_HINT: Record<LookupState, string> = {
  idle: '10 digits, or 10 digits-NNN for a branch. The company details fill in automatically.',
  loading: 'Looking up the company…',
  found: 'Company name and address filled from the tax registry. Please check them.',
  notFound: 'No company found for this tax code. Enter the details by hand.',
  failed: 'Could not reach the tax registry. Enter the details by hand.',
};

/**
 * The question asked before a paid table is closed: is this a walk-in sale
 * ("bán lẻ" — the invoice goes to an anonymous customer) or does the guest want
 * the invoice made out to a company? The order is only closed once the cashier
 * has answered, so nobody can skip it by habit; the answer is saved on the
 * order first, then the table is closed by the caller.
 */
export function CloseOrderDialog({
  order,
  open,
  closing,
  onClose,
  onConfirm,
  purpose = 'close',
}: {
  order: Order;
  open: boolean;
  /** 'close' asks before closing a table; 'issue' before issuing a closed order's invoice from History. */
  purpose?: 'close' | 'issue';
  /** The caller's close/issue mutation is in flight. */
  closing: boolean;
  onClose: () => void;
  /** Called once the buyer choice has been saved; the caller closes the table. */
  onConfirm: () => void;
}) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const [mode, setMode] = useState<Mode>('RETAIL');
  const [draft, setDraft] = useState<Draft>(EMPTY);

  const [lookup, setLookup] = useState<LookupState>('idle');
  // The tax code the company details were last filled from; a saved buyer counts,
  // so reopening the dialog never overwrites details the cashier already confirmed.
  const lookedUp = useRef('');

  useEffect(() => {
    if (!open) return;
    setMode(order.invoiceBuyer ? 'COMPANY' : 'RETAIL');
    setDraft(draftFromOrder(order));
    setLookup('idle');
    lookedUp.current = order.invoiceBuyer?.taxCode?.trim() ?? '';
  }, [open, order]);

  // Auto-fill the company name and address once a complete tax code is typed.
  const taxCode = draft.taxCode.trim();
  useEffect(() => {
    if (!open || mode !== 'COMPANY') return;
    if (!TAX_CODE_PATTERN.test(taxCode)) {
      setLookup('idle');
      return;
    }
    if (taxCode === lookedUp.current) {
      setLookup((current) => (current === 'loading' ? 'idle' : current));
      return;
    }
    const controller = new AbortController();
    const timer = setTimeout(() => {
      setLookup('loading');
      lookupCompanyByTaxCode(taxCode, controller.signal)
        .then((info) => {
          lookedUp.current = taxCode;
          if (!info) {
            setLookup('notFound');
            return;
          }
          setDraft((current) => ({ ...current, company: info.company, address: info.address || current.address }));
          setLookup('found');
        })
        .catch((caught: unknown) => {
          if (controller.signal.aborted) return;
          console.warn(caught);
          setLookup('failed');
        });
    }, 400);
    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [open, mode, taxCode]);

  const companyValid = mode === 'RETAIL' || draft.company.trim().length > 0 || draft.taxCode.trim().length > 0;

  const save = useMutation({
    mutationFn: () => api.orders.setInvoiceBuyer(order.id, mode === 'RETAIL' ? EMPTY : draftToBody(draft)),
    onSuccess: (updated) => {
      queryClient.setQueryData(['order', updated.id], updated);
      void queryClient.invalidateQueries({ queryKey: ['order'] });
      onConfirm();
    },
    onError: (caught: unknown) =>
      toast.error(caught instanceof ApiError ? caught.message : 'Could not save the invoice customer.'),
  });

  const set = (key: keyof Draft) => (event: { target: { value: string } }) =>
    setDraft((current) => ({ ...current, [key]: event.target.value }));

  const busy = save.isPending || closing;

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={purpose === 'close' ? 'Close table — who is the invoice for?' : 'Issue invoice — who is it for?'}
      subtitle={
        purpose === 'close'
          ? `Order #${order.orderNumber} · an e-invoice is issued when the table closes`
          : `Order #${order.orderNumber} · the invoice is issued to EasyInvoice straight away`
      }
      size="md"
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={busy}>
            Back
          </Button>
          <Button variant="primary" size="lg" loading={busy} disabled={!companyValid} onClick={() => save.mutate()}>
            {purpose === 'close'
              ? mode === 'RETAIL'
                ? 'Close as walk-in sale'
                : 'Close and invoice this company'
              : mode === 'RETAIL'
                ? 'Issue as walk-in sale'
                : 'Issue to this company'}
          </Button>
        </>
      }
    >
      <div className="grid gap-3 sm:grid-cols-2">
        <ChoiceCard
          active={mode === 'RETAIL'}
          onClick={() => setMode('RETAIL')}
          icon={<UserRound size={20} />}
          title="Khách lẻ"
          detail="Bán lẻ — no customer details. Invoice reads “Bán cho người tiêu dùng”."
        />
        <ChoiceCard
          active={mode === 'COMPANY'}
          onClick={() => setMode('COMPANY')}
          icon={<Building2 size={20} />}
          title="Xuất hóa đơn công ty"
          detail="The guest wants the invoice in a company’s name and tax code."
        />
      </div>

      {mode === 'COMPANY' ? (
        <div className="mt-5 grid gap-4 sm:grid-cols-2">
          <Field
            label="Tax code (Mã số thuế)"
            hint={LOOKUP_HINT[lookup]}
            error={lookup === 'notFound' || lookup === 'failed' ? LOOKUP_HINT[lookup] : undefined}
          >
            <Input value={draft.taxCode} onChange={set('taxCode')} inputMode="numeric" placeholder="0316162487-003" autoFocus />
          </Field>
          <Field label="Buyer name (Người mua)">
            <Input value={draft.name} onChange={set('name')} />
          </Field>
          <Field label="Company (Tên đơn vị)" className="sm:col-span-2">
            <Input
              value={draft.company}
              onChange={set('company')}
              placeholder={lookup === 'loading' ? 'Looking up…' : 'CÔNG TY TNHH …'}
              disabled={lookup === 'loading'}
            />
          </Field>
          <Field label="Address (Địa chỉ)" className="sm:col-span-2">
            <Input value={draft.address} onChange={set('address')} />
          </Field>
          <Field label="Email" hint="EasyInvoice sends the invoice link here." className="sm:col-span-2">
            <Input type="email" value={draft.email} onChange={set('email')} />
          </Field>
          {!companyValid ? (
            <p className="text-xs text-danger sm:col-span-2">Enter at least the company name or the tax code.</p>
          ) : null}
        </div>
      ) : null}
    </Modal>
  );
}

function ChoiceCard({
  active,
  onClick,
  icon,
  title,
  detail,
}: {
  active: boolean;
  onClick: () => void;
  icon: React.ReactNode;
  title: string;
  detail: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={clsx(
        'flex flex-col items-start gap-2 rounded-xl border p-4 text-left transition',
        active
          ? 'border-accent bg-accent/10 text-ink'
          : 'border-line text-ink-soft hover:border-accent/50 hover:text-ink',
      )}
    >
      <span className={active ? 'text-accent-strong' : 'text-ink-faint'}>{icon}</span>
      <span className="font-display text-lg leading-tight">{title}</span>
      <span className="text-xs text-ink-faint">{detail}</span>
    </button>
  );
}

/** One-line summary for the payment screens: who the invoice will name. */
export function InvoiceBuyerSummary({ order }: { order: Order }) {
  const buyer = order.invoiceBuyer;
  const label = buyer
    ? [buyer.company ?? buyer.name, buyer.taxCode ? `MST ${buyer.taxCode}` : null].filter(Boolean).join(' · ')
    : 'Khách lẻ (bán cho người tiêu dùng)';
  return (
    <p className="text-xs text-ink-faint">
      <span className="font-semibold uppercase tracking-[0.15em]">Invoice to:</span> {label}
    </p>
  );
}
